'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { toastTitle } from '@/lib/toast-title'
import type { Route } from 'next'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  ButtonLink,
  CountBadge,
  Field,
  IconButton,
  InlineEdit,
  Input,
  NumberedSection,
  PageDescription,
  screenTitleClassName,
  SettingRow,
  type InlineEditHandle,
  toast,
} from '@kiao-voice/ui-kit'
import { useOrganizationIdentity } from '@kiao-voice/hooks'
import { getActiveContext, type OrganizationContext, type OrganizationContextOwner } from '@kiao-voice/api-client'
import { fetchMe, readStoredToken } from '../auth/auth-storage'
import { WorkspacePageHeader } from '../../components/workspace-indicator'
import { resolveActiveContext } from '../../lib/active-context-resolver'
import {
  createOrganization,
  getOrganization,
  getTeamPlan,
  listContexts,
  TeamApiError,
  seatPaymentRefusalMessageKey,
  teamErrorMessageKey,
  type TeamPlanPricing,
} from './api-client'
import { AddMemberSection } from './add-member-section'
import { MembersSection } from './members-section'
import { SeatSection } from './seat-section'
import { resolveHandoffReminder, resolveTeamBillingMode } from './team-billing-state'
import { TrialSeatSection } from './trial-seat-section'
import { LapsedTeamRecovery } from './lapsed-team-recovery'
import { TeamSubscribeModal } from './team-subscribe-modal'

interface TeamPageProps {
  /** Test seams. */
  getTokenFn?: () => string | null
  userEmail?: string | null
  /** Test seam — drives the 3DS redirect during a seat charge. */
  redirect?: (url: string) => void
  /** Test seam — defaults to `GET /organizations/contexts` (drives per-context view). */
  fetchContextsFn?: typeof listContexts
  /** Test seam — drives the team-member redirect to /account. */
  navigate?: <T extends string>(path: Route<T>) => void
}

function isOwnerContext(ctx: OrganizationContext): ctx is OrganizationContextOwner {
  return ctx.organization !== null && 'seatUsage' in ctx
}

/**
 * Personal-context view of /account/team: there's no in-app team to manage, so
 * point the user at the public pricing page's team section to start one
 * (Claude parity — personal context is upsell, not management).
 */
function TeamUpsell() {
  const { t } = useTranslation('web')
  return (
    <>
      <WorkspacePageHeader title={t('team.upsellHeading')} subtitle={t('team.upsellBody')} />
      <section className="max-w-[480px]" data-testid="team-upsell">
        <p className="text-sm text-muted">{t('team.upsellDesc')}</p>
        <div className="mt-[var(--gap-md)]">
          <ButtonLink
            href="/pricing#team"
            target="_blank"
            rel="noreferrer"
            data-testid="team-upsell-cta"
          >
            {t('team.upsellCta')}{' '}
            <span className="meta" aria-hidden="true">
              ↗
            </span>
          </ButtonLink>
        </div>
      </section>
    </>
  )
}

export function TeamPage(props: TeamPageProps) {
  // The confirm provider (seat charges / member removals, gated by the shared
  // .kiao-dialog — never window.confirm) is now mounted once by the (app)
  // route-group layout. Tests that render TeamPage in isolation supply their own
  // KiaoConfirmProvider.
  return <TeamPageContent {...props} />
}

function TeamPageContent({
  getTokenFn = readStoredToken,
  userEmail = null,
  redirect,
  fetchContextsFn = listContexts,
  navigate,
}: TeamPageProps) {
  const { t } = useTranslation('web')
  const router = useRouter()
  const [context, setContext] = useState<OrganizationContext | null>(null)
  const [pricing, setPricing] = useState<TeamPlanPricing | null>(null)
  const [pricingError, setPricingError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  // Pre-fill the checkout's cardholder email with the signed-in user
  // (same convenience the pricing page offers).
  const [resolvedEmail, setResolvedEmail] = useState<string | null>(userEmail)
  // The switchable contexts load inside the shared hook, which exposes no "done"
  // signal; flip this once the loader settles (success OR failure) so the render
  // gate waits for BOTH /me and the contexts before branching — otherwise /me
  // resolving first would flash the owner/NoTeam view to a member before the
  // redirect lands.
  const [contextsReady, setContextsReady] = useState(false)
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const token = getTokenFn()

  // Switchable contexts via the shared hook — display-only here (the app shell
  // owns persisting the smart default to the store). The resolved context drives
  // which view this page renders (personal upsell / member redirect / owner
  // management) and re-resolves when the user switches workspaces.
  const loadContexts = useCallback(
    () =>
      fetchContextsFn()
        .then((contexts) => ({ contexts }))
        .finally(() => setContextsReady(true)),
    [fetchContextsFn]
  )
  const org = useOrganizationIdentity({ enabled: Boolean(token), loadContexts })

  // Navigation seam: prefer the injected test seam, fall back to the router.
  const go = useCallback(
    <T extends string>(path: Route<T>) => {
      if (navigate) navigate(path)
      else router.push(path)
    },
    [navigate, router]
  )

  useEffect(() => {
    if (userEmail || !token) return
    let cancelled = false
    void fetchMe(token)
      .then((me) => {
        if (!cancelled) setResolvedEmail(me.user.email)
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, { source: 'features/team/team-page.tsx' })
        // Cardholder email is a convenience pre-fill; ignore failures.
      })
    return () => {
      cancelled = true
    }
  }, [userEmail, token])

  // Load the org-management view (/me). The switchable contexts come from the
  // hook; this only fetches the owner data and re-runs on a workspace switch.
  const reload = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      setContext(await getOrganization())
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'features/team/team-page.tsx' })
      toast.error(toastTitle(t('team.errGeneric')))
    } finally {
      setLoading(false)
    }
  }, [token, t])

  // Re-run the /me loader when the user switches workspaces so the per-context
  // view (and its data) stays in sync with the selected context.
  useEffect(() => {
    void reload()
  }, [reload, org.activeKey])

  const loadPricing = useCallback(() => {
    if (!token) return
    setPricingError(false)
    setPricing(null)
    void getTeamPlan()
      .then(setPricing)
      .catch((caughtError) => {
        reportHandledException(caughtError, { source: 'features/team/team-page.tsx' })
        setPricingError(true)
        setPricing(null)
      })
  }, [token])

  useEffect(() => { loadPricing() }, [loadPricing])

  const recoverTeam = async (teamId: number) => {
    const isCurrentSession = () => mounted.current && getTokenFn() === token
    if (!isCurrentSession()) return
    setSubscribeOpen(false)
    try {
      const freshContexts = await fetchContextsFn()
      if (!isCurrentSession()) return
      const restored = freshContexts.find((item) =>
        item.organizationId === teamId && item.mode === 'team' && item.role === 'owner')
      // Use fresh server authority, and do not override a switch made while loading.
      // Selecting also notifies the app shell's independent contexts hook.
      if (restored && getActiveContext() === 'personal') {
        org.selectContext(restored.key)
        // The active-key effect reloads /me; each subscribed hook refreshes contexts.
        return
      }
    } catch (cause) {
      reportHandledException(cause, { source: 'features/team/team-page.tsx' })
      if (!isCurrentSession()) return
      toast.error(toastTitle(t('team.errGeneric')))
    }
    org.refetch()
    await reload()
  }

  // What workspace are we acting in? Drives the three branches below
  // (personal upsell / member redirect / owner management). Keyed off the
  // SELECTED context, not the static /me role.
  const resolved = resolveActiveContext(org.contexts, org.activeKey)

  // A team-MEMBER workspace has nothing to manage here — billing & usage live
  // on /account, so redirect there (replaces the old MemberBadge view).
  const isTeamMember = resolved.kind === 'team-member'
  useEffect(() => {
    if (isTeamMember) go('/app/account')
  }, [isTeamMember, go])

  const showError = useCallback(
    (cause: unknown, fallbackUsed?: number) => {
      if (cause instanceof TeamApiError) {
        const used = (cause.body as { used?: number } | null)?.used ?? fallbackUsed ?? 0
        const key = teamErrorMessageKey(cause.code)
        if (key !== 'team.errGeneric') {
          toast.error(toastTitle(t(key, { used })))
          return
        }
        // 402 的 body 沒有 machine code：`updateSeats`／`subscribe` 的銀行拒絕會把
        // `userFacingError`（一句英文銀行訊息）塞進 `error` 欄位，對不到任何已知
        // code。用 status 接住它，否則使用者只看到「發生錯誤，請再試一次」，然後
        // 拿同一張死卡重試。
        if (cause.status === 402) {
          toast.error(t('team.errPaymentFailed'))
          return
        }
      }
      toast.error(toastTitle(t('team.errGeneric')))
    },
    [t]
  )

  if (loading || !context || !contextsReady) {
    return <p className="note">{t('team.processing')}</p>
  }

  // Team-MEMBER workspace — the effect above redirects to /account; render a
  // brief placeholder while the navigation lands.
  if (isTeamMember) {
    return <p className="note">{t('team.processing')}</p>
  }

  // Personal workspace offers direct recovery only for the caller's retained team.
  // New owners keep the existing pricing upsell.
  if (resolved.kind === 'personal') {
    const team = context.lapsedTeam
    if (!team) return <TeamUpsell />
    return <>
      <LapsedTeamRecovery team={team} pricingReady={Boolean(pricing)} pricingError={pricingError} onRetry={loadPricing}
        onSubscribe={() => setSubscribeOpen(true)} />
      {pricing && <TeamSubscribeModal key={team.id} open={subscribeOpen}
        pricing={{ ...pricing, minSeats: Math.max(pricing.minSeats, team.memberCount) }}
        userEmail={resolvedEmail} initialSeats={team.memberCount}
        initialTeamName={team.name.includes('@') ? '' : team.name} resetKey={`recovery-${team.id}`}
        onClose={() => setSubscribeOpen(false)} onSuccess={() => void recoverTeam(team.id)}
        getTokenFn={getTokenFn} />}
    </>
  }

  // Owner management. The `unknown` fallback (contexts failed to load) defers
  // to the legacy /me shape: an owner context manages, anything else is treated
  // as having no manageable team and offered the create flow.
  if (!isOwnerContext(context)) {
    // No manageable team yet (and not a resolved member/personal) — offer to
    // name one. Covers the contexts-unavailable fallback.
    return <NoTeam onCreated={reload} getTokenFn={getTokenFn} />
  }

  const owner = context
  const subscription = owner.subscription
  const billingMode = subscription ? resolveTeamBillingMode(subscription) : null
  const handoffReminder = subscription
    ? resolveHandoffReminder(subscription, owner.members, owner.seatUsage)
    : null
  // P1 names implicit orgs after the owner's email ("org of 1"); rendering
  // a raw email as the page title reads broken. Show a localized
  // placeholder until the team is actually named (first subscribe).
  const orgDisplayName = owner.organization.name.includes('@')
    ? t('team.untitledOrg')
    : owner.organization.name

  return (
    <>
      <header className="mb-[var(--gap-lg)]">
        <OrgTitle
          name={orgDisplayName}
          getTokenFn={getTokenFn}
          onRenamed={reload}
          onError={showError}
        />
        <PageDescription>{t('team.lead')}</PageDescription>
      </header>

      {handoffReminder ? (
        // 轉正後的提醒只讀成員狀態，不做任何動作；啟用與否由 owner 在成員區決定。
        <Alert tone="warn" className="mb-[var(--gap-lg)]" data-testid="team-handoff-reminder">
          <b>{t('team.handoffReminderTitle')}</b> {t(handoffReminder.key, handoffReminder.params)}{' '}
          <a className="link" href="#team-members">
            {t('team.handoffReminderCta')}
          </a>
        </Alert>
      ) : null}

      {/* 三節包一層：`NumberedSection` 的髮絲線是「段與段之間」的分層，`first:border-t-0`
          要 01 節真的是第一個子元素才生效——直接放在 <header> 後面會多一條線。 */}
      <div>
        <NumberedSection
          number="01"
          id="team-seats"
          title={t('team.sectionSeatsTitle')}
          description={t('team.sectionSeatsDesc')}
          data-testid="team-seats-section"
        >
          {subscription && billingMode?.kind === 'trial' ? (
            <TrialSeatSection
              subscription={subscription}
              mode={billingMode}
              used={owner.seatUsage.used}
              redirect={redirect}
              onDone={reload}
              onError={showError}
            />
          ) : subscription && billingMode?.kind === 'trial_ended_pending' ? (
            <SettingRow
              label={t('team.trialEndedPendingTitle')}
              description={t('team.trialEndedPendingBody')}
              control={null}
              data-testid="trial-ended-pending"
            />
          ) : subscription ? (
            <>
              {billingMode?.kind === 'paid' &&
              seatPaymentRefusalMessageKey(billingMode.adjustBlockedReason) ? (
                <Alert tone="warn" className="mb-[var(--gap-sm)]" data-testid="seat-adjust-blocked">
                  {t(seatPaymentRefusalMessageKey(billingMode.adjustBlockedReason)!)}
                </Alert>
              ) : null}
              <SeatSection
                seats={subscription.seats ?? 0}
                pendingSeats={subscription.pendingSeats ?? null}
                used={owner.seatUsage.used}
                available={owner.seatUsage.available}
                perSeatPriceTwd={subscription.perSeatPriceTwd ?? null}
                cycle={subscription.cycle}
                periodStart={subscription.currentPeriodStart ?? null}
                periodEnd={subscription.currentPeriodEnd ?? null}
                cancelAtPeriodEnd={subscription.cancelAtPeriodEnd === true}
                redirect={redirect}
                onDone={reload}
                onError={showError}
              />
            </>
          ) : (
            // No subscription yet — the owner must start the team before adding anyone.
            <SettingRow
              label={t('team.noSubscriptionLabel')}
              control={
                <Button
                  variant="primary"
                  onClick={() => setSubscribeOpen(true)}
                  disabled={!pricing}
                >
                  {t('team.subscribeCta')}
                </Button>
              }
            />
          )}
        </NumberedSection>

        {subscription ? (
          <NumberedSection
            number="02"
            title={t('team.sectionAddMemberTitle')}
            data-testid="team-add-member-section"
          >
            <AddMemberSection
              organizationId={owner.organization.id}
              ownerEmail={
                resolvedEmail ??
                owner.members.find((member) => member.role === 'owner')?.email ??
                null
              }
              currentSeats={subscription.seats ?? 0}
              seatsAvailable={owner.seatUsage.available}
              perSeatPriceTwd={subscription.perSeatPriceTwd ?? null}
              cycle={subscription.cycle}
              periodStart={subscription.currentPeriodStart ?? null}
              periodEnd={subscription.currentPeriodEnd ?? null}
              getTokenFn={getTokenFn}
              redirect={redirect}
              onDone={reload}
              onError={showError}
            />
          </NumberedSection>
        ) : null}

        <NumberedSection
          number="03"
          id="team-members"
          title={
            <span className="inline-flex items-center gap-[var(--gap-xs)]">
              {t('team.membersHeading')}
              <CountBadge count={owner.seatUsage.used} />
            </span>
          }
          description={t('team.membersNote')}
          data-testid="team-members-section"
        >
          <MembersSection
            organizationId={owner.organization.id}
            members={owner.members}
            currentEmail={resolvedEmail}
            viewerIsOwner
            getTokenFn={getTokenFn}
            onRevoked={reload}
            onAccessChanged={reload}
            onError={showError}
          />
        </NumberedSection>
      </div>

      {pricing && (
        <TeamSubscribeModal
          open={subscribeOpen}
          pricing={pricing}
          userEmail={resolvedEmail}
          resetKey="account-team"
          onClose={() => setSubscribeOpen(false)}
          onSuccess={reload}
          getTokenFn={getTokenFn}
        />
      )}
    </>
  )
}

/**
 * Owner-view team title. The heading IS the editor (`<InlineEdit as="h1">`) —
 * POST /organizations is an idempotent name/rename of the caller's own org, so
 * committing the text is the whole rename. The pencil is only a shortcut into
 * that same element (`focusEnd()`), never a second editor.
 */
function OrgTitle({
  name,
  getTokenFn,
  onRenamed,
  onError,
}: {
  name: string
  getTokenFn: () => string | null
  onRenamed: () => void
  onError: (cause: unknown) => void
}) {
  const { t } = useTranslation('web')
  const editRef = useRef<InlineEditHandle>(null)
  const renameLabel = t('team.renameCta')

  const rename = useCallback(
    async (next: string) => {
      const token = getTokenFn()
      if (!token) throw new Error('Not authenticated')
      await createOrganization(next)
      onRenamed()
    },
    [getTokenFn, onRenamed]
  )

  return (
    <div className="flex items-center gap-[var(--gap-xs)]">
      <InlineEdit
        ref={editRef}
        as="h1"
        className={screenTitleClassName}
        aria-label={t('team.createTeamNameLabel')}
        data-testid="rename-org-input"
        maxLength={120}
        value={name}
        onCommit={rename}
        onError={onError}
      />
      <IconButton
        variant="ghost"
        data-testid="rename-org"
        aria-label={renameLabel}
        title={renameLabel}
        onClick={() => editRef.current?.focusEnd()}
      >
        <Pencil size={17} aria-hidden="true" />
      </IconButton>
    </div>
  )
}

function NoTeam({
  onCreated,
  getTokenFn,
}: {
  onCreated: () => void
  getTokenFn: () => string | null
}) {
  const { t } = useTranslation('web')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const create = async () => {
    const token = getTokenFn()
    if (!token || !name.trim()) return
    setBusy(true)
    try {
      await createOrganization(name.trim())
      toast.success(t('team.seatsUpdated'))
      onCreated()
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'features/team/team-page.tsx' })
      toast.error(toastTitle(t('team.errGeneric')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <WorkspacePageHeader title={t('team.noTeamHeading')} subtitle={t('team.noTeamBody')} />
      <section className="max-w-[480px]">
        <Field label={t('team.createTeamNameLabel')} htmlFor="create-team-name">
          <Input
            id="create-team-name"
            value={name}
            disabled={busy}
            placeholder={t('team.createTeamNamePlaceholder')}
            data-testid="create-team-name"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <div className="mt-[var(--gap-md)]">
          <Button variant="primary" onClick={create} disabled={busy || !name.trim()}>
            {t('team.createTeamCta')}
          </Button>
        </div>
      </section>
    </>
  )
}
